import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  ChevronRight,
  Loader2,
  ShieldCheck,
  Ticket,
  UserCheck,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import type {
  ResolutionOp,
  ResolutionState,
  ResolutionStep,
} from '@/lib/disruptions/types';
import {
  rebookingPolicyGate,
  skippedIsPositive,
  stepCounter,
  stepIcon,
  stepState,
  stepSubject,
} from '../resolution-steps';
import { RebookingPolicyNotice } from '../rebooking-policy-notice';
import { RosterBulkBar } from '../roster-bulk-bar';
import { EventAllocationActionBar } from '../event-allocation-action-bar';
import { EventVoucherActionBar } from '../event-voucher-action-bar';
import { AutomaticAcceptancePanel } from '../automatic-acceptance-panel';
import { RoleGate } from '@/auth/role-gate';
import { Role } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { useContainDisruption, useForceAcceptAllDisruption, useIssueDisruptionTickets } from '@/lib/disruptions/use-disruptions';

interface DisruptionPipelineStageProps {
  disruptionUrn: string;
  state: ResolutionState;
  selectedOp: ResolutionOp | null;
  onSelectOp: (op: ResolutionOp | null) => void;
}

export function DisruptionPipelineStage({
  disruptionUrn,
  state,
  selectedOp,
  onSelectOp,
}: DisruptionPipelineStageProps) {
  const { t } = useTranslation('tower');
  const recommendedOp = state.nextStep.op;
  const activeOp = selectedOp ?? (recommendedOp !== 'none' ? recommendedOp : null);

  return (
    <div className="flex flex-col gap-3" data-testid="disruption-pipeline-stage">
      {/* 6-Stage Interactive Visual Pipeline */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
        {state.steps.map((step, idx) => {
          const isRecommended = recommendedOp === step.op;
          const isSelected = activeOp === step.op;
          const isRunning = step.status === 'RUNNING';
          const isDone = step.status === 'DONE';
          const isPositiveSkipped = skippedIsPositive(step);
          const Icon = stepIcon(step.op);
          const st = stepState(step, state.kpis.groups ?? null, state.steps);

          return (
            <button
              key={step.op}
              type="button"
              onClick={() => onSelectOp(isSelected && !isRecommended ? null : step.op)}
              className={cn(
                "group relative flex flex-col justify-between rounded-xl border p-3 text-left transition-all duration-200 focus:outline-none",
                isSelected
                  ? "border-primary bg-primary/[0.03] ring-2 ring-primary/30 shadow-md"
                  : isRecommended
                    ? "border-primary/60 bg-card hover:border-primary hover:shadow-sm"
                    : "border-border bg-card hover:border-muted-foreground/40 hover:bg-muted/20"
              )}
            >
              {/* Top Row: Index Badge & Status Icon */}
              <div className="flex items-center justify-between w-full">
                <span className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold transition-transform group-hover:scale-105",
                  isDone || isPositiveSkipped
                    ? "bg-emerald-500 text-white"
                    : isRunning
                      ? "bg-sky-500 text-white animate-pulse"
                      : isRecommended
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                )}>
                  {isDone || isPositiveSkipped ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : isRunning ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    idx + 1
                  )}
                </span>

                {isRecommended ? (
                  <Badge variant="neutral" className="text-[10px] px-1.5 py-0 h-4 bg-primary text-primary-foreground font-semibold uppercase tracking-wider">
                    {t('alt.pipeline.next')}
                  </Badge>
                ) : (
                  <Icon className={cn(
                    "h-4 w-4",
                    isDone ? "text-emerald-500" : isRunning ? "text-sky-500" : "text-muted-foreground"
                  )} />
                )}
              </div>

              {/* Title & State */}
              <div className="mt-2 space-y-0.5">
                <p className="text-xs font-bold text-foreground group-hover:text-primary transition-colors">
                  {stepSubject(step.op)}
                </p>
                <p className="text-[11px] font-medium text-muted-foreground truncate" title={st.label}>
                  {st.label}
                </p>
              </div>

              {/* Progress Count */}
              <div className="mt-2 pt-1.5 border-t border-border/50 flex items-center justify-between text-[11px]">
                <span className="font-mono text-xs font-semibold tabular-nums text-foreground">
                  {stepCounter(step)}
                </span>
                <ChevronRight className={cn(
                  "h-3 w-3 text-muted-foreground transition-transform duration-200",
                  isSelected ? "rotate-90 text-primary" : "group-hover:translate-x-0.5"
                )} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Expanded Active Step Deck */}
      {activeOp ? (
        <ExpandedStepDeck 
          disruptionUrn={disruptionUrn}
          op={activeOp}
          state={state}
        />
      ) : null}
    </div>
  );
}

function ExpandedStepDeck({
  disruptionUrn,
  op,
  state,
}: {
  disruptionUrn: string;
  op: ResolutionOp;
  state: ResolutionState;
}) {
  const { t } = useTranslation('tower');
  const step = state.steps.find((s) => s.op === op);
  if (!step) return null;

  const st = stepState(step, state.kpis.groups ?? null, state.steps);
  const Icon = stepIcon(op);

  return (
    <Card className="border-primary/40 bg-card/60 backdrop-blur-sm shadow-md animate-in fade-in-50 duration-200">
      <CardContent className="p-4 space-y-3">
        {/* Step Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">
                {t('alt.pipeline.manage', { subject: stepSubject(op) })}
              </h3>
              <p className="text-xs text-muted-foreground">
                {st.detail || step.reason || t('alt.pipeline.defaultDetail')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Badge variant="neutral" className="text-xs font-mono">
              {stepCounter(step)}
            </Badge>
          </div>
        </div>

        {/* Action Controls for this step */}
        <div className="pt-1">
          <StepActionRouter disruptionUrn={disruptionUrn} op={op} step={step} state={state} />
        </div>
      </CardContent>
    </Card>
  );
}

function StepActionRouter({
  disruptionUrn,
  op,
  step,
  state,
}: {
  disruptionUrn: string;
  op: ResolutionOp;
  step: ResolutionStep;
  state: ResolutionState;
}) {
  const { t } = useTranslation('tower');
  switch (op) {
    case 'rebooking': {
      // Pre-run policy gate (rebooking TDS §18) — same rule as Tower and the
      // Overview stepper: the station's policy has the engine off, so neither the
      // bulk trigger nor the advanced panel is offered; the rule is named instead.
      const gate = rebookingPolicyGate(state.steps);
      if (gate.disabled) {
        return (
          <RebookingPolicyNotice
            policyUrn={gate.policyUrn}
            data-testid="pipeline-rebooking-policy-gate"
          />
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <RosterBulkBar disruptionUrn={disruptionUrn} />
          <details className="rounded-md border border-border bg-muted/20 px-3 py-2 text-xs">
            <summary className="cursor-pointer select-none font-medium text-foreground hover:text-primary transition-colors">
              {t('alt.pipeline.advanced')}
            </summary>
            <div className="pt-2">
              <AutomaticAcceptancePanel disruptionUrn={disruptionUrn} />
            </div>
          </details>
        </div>
      );
    }
    case 'tickets':
      return <TicketsActionBar disruptionUrn={disruptionUrn} step={step} />;
    case 'hotels':
      return <EventAllocationActionBar disruptionUrn={disruptionUrn} />;
    case 'vouchers':
      return <EventVoucherActionBar disruptionUrn={disruptionUrn} />;
    case 'acceptance':
      return <AcceptanceActionBar disruptionUrn={disruptionUrn} step={step} state={state} />;
    case 'contain':
      return <ContainActionBar disruptionUrn={disruptionUrn} step={step} />;
    default:
      return null;
  }
}

function TicketsActionBar({ disruptionUrn, step }: { disruptionUrn: string; step: ResolutionStep }) {
  const { t } = useTranslation('tower');
  const issue = useIssueDisruptionTickets(disruptionUrn);
  const n = step.remaining ?? step.total ?? 0;
  const busy = issue.isPending || step.status === 'RUNNING';

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-3 text-sm">
      <div className="space-y-0.5">
        <p className="font-medium text-foreground">
          {step.status === 'BLOCKED' ? step.reason : t('alt.tickets.ready', { count: n })}
        </p>
        <p className="text-xs text-muted-foreground">{t('alt.tickets.note')}</p>
        {issue.data ? (
          <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
            {t('alt.tickets.result', {
              issued: issue.data.tally.issued ?? 0,
              already: issue.data.tally.alreadyTicketed ?? 0,
            })}
          </p>
        ) : null}
      </div>

      <Button
        size="sm"
        onClick={() => issue.mutate()}
        disabled={busy || step.status === 'BLOCKED' || step.status === 'SKIPPED' || n === 0}
        className="gap-1.5"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ticket className="h-3.5 w-3.5" />}
        {busy ? t('alt.tickets.issuing') : t('alt.tickets.cta', { count: n })}
      </Button>
    </div>
  );
}

function AcceptanceActionBar({
  disruptionUrn,
  step,
  state,
}: {
  disruptionUrn: string;
  step: ResolutionStep;
  state: ResolutionState;
}) {
  const { t } = useTranslation('tower');
  const accept = useForceAcceptAllDisruption(disruptionUrn);
  const g = state.kpis.groups;
  const awaiting = step.remaining ?? g?.awaiting ?? 0;
  const busy = accept.isPending || step.status === 'RUNNING';

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-3 text-sm">
      <div className="space-y-0.5">
        <p className="font-medium text-foreground">
          {g
            ? t('alt.acceptance.accepted', { accepted: g.accepted, atGate: g.atGate })
            : t('alt.acceptance.title')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('alt.acceptance.pending', { count: awaiting })}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <RoleGate min={Role.OPS_SUPERVISOR}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => accept.mutate()}
            disabled={busy || awaiting === 0}
            className="gap-1.5"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />}
            {busy ? t('alt.acceptance.accepting') : t('alt.acceptance.acceptAll', { count: awaiting })}
          </Button>
        </RoleGate>
      </div>
    </div>
  );
}

function ContainActionBar({ disruptionUrn, step }: { disruptionUrn: string; step: ResolutionStep }) {
  const { t } = useTranslation('tower');
  const contain = useContainDisruption(disruptionUrn);
  const [confirming, setConfirming] = useState(false);
  const mode = step.containMode;
  const isDone = step.status === 'DONE';
  const waiting = step.status === 'BLOCKED' && mode === 'TRAVELLING';
  const label =
    mode === 'NATURAL' ? t('alt.contain.confirmClosure') : t('alt.contain.forceImmediate');

  if (isDone) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-950/20 p-3 text-emerald-800 dark:text-emerald-300 text-sm">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <span className="font-semibold">{t('alt.contain.done')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-3 text-sm">
      <div className="space-y-0.5">
        <p className="font-medium text-foreground">
          {waiting ? t('alt.contain.waiting') : t('alt.contain.formal')}
        </p>
        <p className="text-xs text-muted-foreground">{t('alt.contain.note')}</p>
      </div>

      <div className="flex items-center gap-2">
        {confirming ? (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
              {t('common:common.cancel')}
            </Button>
            <Button
              size="sm"
              variant={mode === 'NATURAL' ? 'default' : 'destructive'}
              onClick={() => {
                setConfirming(false);
                contain.mutate(undefined);
              }}
              disabled={contain.isPending}
            >
              {contain.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="mr-1 h-3.5 w-3.5" />}
              {label}
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            onClick={() => setConfirming(true)}
            disabled={contain.isPending || waiting}
            className="gap-1.5"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {label}
          </Button>
        )}
      </div>
    </div>
  );
}
